// Per-stage fit lookup.
//
// Reads the engine-computed `fitProfile.by_stage[learnerStage]` shipped on
// every `Book` since BE chunk 1 (2026-06-03,
// hs_backend_service_v3.0/specs/library_feature_frontend_handoff.md §3).
//
// Returns `null` when fit can't be determined — render no badge in that case.
// Critical: do NOT fall back to a default classification. The legacy
// `estimateGuideFit` defaulted to `comfort` when `recommendedStage` was null,
// which produced the "every card says Comfort Read" bug this helper exists to fix.

import type { FitClassification, FitProfile, ChallengeLevel } from '@/types'

export interface FitDisplay {
  classification: FitClassification
  matchScore: number              // 0–100
  decodingPercentage: number      // 0–100
  challengeLevel: ChallengeLevel
  challengeLine: string           // BE-supplied per-stage copy
}

export function lookupFit(
  fitProfile: FitProfile | null | undefined,
  learnerStage: number | null | undefined,
): FitDisplay | null {
  if (!fitProfile?.by_stage) return null
  if (learnerStage == null) return null
  // Defensive — stage should be 0..5, but a stray value would silently miss
  // the by_stage map. Range-check before the lookup so the failure mode is
  // "no badge" rather than a TypeScript-runtime mismatch.
  if (learnerStage < 0 || learnerStage > 5 || !Number.isInteger(learnerStage)) {
    return null
  }
  const key = String(learnerStage) as '0' | '1' | '2' | '3' | '4' | '5'
  const entry = fitProfile.by_stage[key]
  if (!entry) return null
  return {
    classification: entry.classification,
    matchScore: entry.match_score,
    decodingPercentage: entry.decoding_percentage,
    challengeLevel: entry.challenge_level,
    challengeLine: entry.challenge_line,
  }
}
